/* @flow */
import Platform from "nexia_mobile/platform";
import sleep from "nx-components/utils/sleep";
import { history } from "nexia_mobile/history";

export function currentlyConfiguringDragonfly() {
  return (
    history &&
    history.location &&
    history.location.pathname &&
    history.location.pathname.startsWith("/app/dragonfly/")
  );
}

const HOST = "http://10.10.10.1:80";

// Change this to true to test dragonfly wizards in the browser
const stub = false;

function doCommand({
  flags,
  command,
  data,
}: {
  flags?: number,
  command: string,
  data?: string,
}) {
  return Platform.nativeHTTPRequest({
    method: "POST",
    url: `${HOST}/command`,
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      flags: flags || 0,
      command,
      data,
    }),
  })
    .then(({ code, body }) => {
      if (code >= 400) {
        throw new Error(`Dragonfly HTTP error: ${code}`);
      }
      return JSON.parse(body);
    })
    .then((result) => {
      if (result.code !== 0) {
        throw new Error(result.response);
      }
      return result;
    });
}

type WiFiNetwork = {|
  id: number | string,
  ssid: string,
  secure: boolean,
|};

// Network list looks like eg. this:
// ! 24 networks found
// !  # Ch RSSI MAC (BSSID)        Rate Security   Mode Len Network (SSID)
// #  0  1  -58 4C:55:CC:12:8C:99     0 WPA2-Mixed    -  12 ZentriOS-C99
// #  1  1  -74 04:62:73:52:EA:60     0 WPA2-Mixed    -   5 pluto
// #  2  1  -65 10:0D:7F:73:44:FE     0 Open          -  27 Free Legacy WiFi 2.4GHz_EXT
// #  3  1  -71 0E:8D:9B:1C:74:B1     0 Unknown       -  16 SteelWave - Corp
// #  4  1  -67 0E:8D:9B:1C:74:B2     0 WPA2-Mixed    -  17 SteelWave - Guest
// #  5  1  -72 04:62:73:52:EA:62     0 WPA2-Mixed    -   6 pluto5
// #  6  1  -74 04:62:73:52:EA:61     0 WPA2-Mixed    -   9 altiGuest
// #  7  1  -77 7C:95:F3:74:42:04     0 Unknown       -  11 StnInternal
// #  8  2  -50 38:D5:47:88:FC:20     0 WPA2-AES      -  12 asus-support
// #  9  2  -87 B2:39:56:51:14:F8     0 WPA2-AES      -  15 HemaSourceGuest
// # 10  1  -81 A4:2B:8C:1F:10:28     0 WPA2-Mixed    -  21 SteelWave - Guest_EXT
// # 11  6  -59 80:2A:A8:14:4B:F8     0 WPA2-AES      -   8 nexia_24
// # 12  6  -79 28:CF:DA:B2:65:89     0 WPA2-Mixed    -   6 acus11
// # 13  6  -55 80:2A:A8:14:4B:F7     0 WPA2-AES      -   8 nexia_24
// # 14  6  -50 DC:9B:9C:F1:5A:B2     0 WPA2-AES      -  12 nexia_test_1
// # 15  6  -83 78:F2:9E:7E:39:7B     0 Unknown       -   7 XFINITY
// # 16  6  -84 04:62:73:40:5C:C0     0 WPA2-Mixed    -   5 pluto
// # 17  1  -79 7C:95:F3:74:42:02     0 WPA2-AES      -  17 secure1120-edge-I
// # 18  9  -56 08:60:6E:CD:68:20     0 WPA2-AES      -   4 boom
// # 19 11  -80 10:05:CA:0D:5C:04     0 Unknown       -  11 StnInternal
// # 20 11  -66 10:05:CA:64:26:E4     0 Unknown       -  11 StnInternal
// # 21 11  -81 10:05:CA:0D:5C:06     0 Open          -   8 StnGuest
// # 22 11  -66 10:05:CA:64:26:E2     0 WPA2-AES      -  17 secure1120-edge-I
// # 23  1  -78 7C:95:F3:74:42:06     0 Open          -   8 StnGuest
//
function parseNetworkList(list: string): Array<WiFiNetwork> {
  const lines = list.split("\n");

  return (
    lines
      // Trim off first two lines and last line
      .slice(2, lines.length - 1)
      .map((line) => {
        return line
          .split(/\s+/) // Split on whitespace
          .slice(1); // Remove first item (empty)
      })
      .map(
        /* eslint-disable no-unused-vars */
        ([
          id,
          channel,
          rssi,
          mac,
          rate,
          security,
          mode,
          length,
          ...ssidParts
          /* eslint-enable no-unused-vars */
        ]) => {
          return {
            id,
            ssid: ssidParts.join(" "),
            secure: /WEP|WPA/.test(security),
          };
        }
      )
  );
}

function dedupeNetworkList(list: Array<WiFiNetwork>): Array<WiFiNetwork> {
  const networksBySSID = {};
  list.forEach((item) => {
    // List returned by dragonfly is ordered by signal strength; we want
    // networks that appear more than once (because they have more than one
    // BSSID) to be ordered based on the signal strength of their best BSSID.
    if (networksBySSID[item.ssid] == null) {
      networksBySSID[item.ssid] = item;
    }
  });
  // Flow force-cast here is because flow's type for Object.values is hardcoded
  // to return mixed since flow doesn't know about property enumerability.
  return ((Object.values(networksBySSID): any): Array<WiFiNetwork>);
}

export function getVisibleWifiNetworks(): Promise<Array<WiFiNetwork>> {
  if (stub) {
    return sleep(300).then(() => [
      { id: "1", ssid: "Network 1 (Open)", secure: false },
      { id: "2", ssid: "Network 2 (Secure)", secure: true },
    ]);
  }

  return doCommand({ command: "wlan_scan -v" }).then(({ response }) => {
    return dedupeNetworkList(parseNetworkList(response));
  });
}

export function setConnectionPinForNexia(pin: string): Promise<void> {
  if (stub) {
    return sleep(400);
  }

  const pinFilename = "nexia.pin";

  return doCommand({
    command: `file_create -o ${pinFilename} ${pin.length}`,
  }).then(({ response }) => {
    const fileDescriptor = response.trim();

    return doCommand({
      flags: 4,
      command: `stream_write ${fileDescriptor} ${pin.length}`,
      data: btoa(pin),
    });
  });
}

export function testCanTalkToDevice(): Promise<void> {
  if (stub) {
    return sleep(400);
  }

  return doCommand({
    command: "get wl s",
  }).then(() => {
    /* discard response */
  });
}

export function disableAdHocAndConnectToNexiaUsingSSIDAndPassword(
  ssid: string,
  password: string = ""
): Promise<void> {
  if (!ssid) {
    return Promise.reject(
      new Error("You must provide an SSID for the thermostat to connect to")
    );
  }

  if (stub) {
    return sleep(2000);
  }

  return doCommand({
    command: `set wl s ${ssid}`,
  })
    .then(() => {
      return doCommand({
        command: `set wl p ${password}`,
      });
    })
    .then(() => {
      return doCommand({
        command: "save",
      });
    })
    .then(() => {
      return doCommand({
        command: "reboot",
      });
    });
}

export function isDragonflyConfigurationPossible(): boolean {
  if (stub) {
    return true;
  }

  return Platform.canUseNativeHTTP();
}



// WEBPACK FOOTER //
// ./src/api/dragonfly.js